import React, { useMemo, useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import { Spinner, Button, Alert } from 'react-bootstrap';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import Swal from 'sweetalert2';
import dayjs from 'dayjs';
import { toast } from 'sonner';
import axios from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils';
import { EmptyState } from '../../Common/EmptyState';
import PageHeader from '../../../layouts/PageHeader';
import { ListToolbar } from '../../Common/ListToolbar';
import Select from 'react-select';
import { brandSelectTheme } from '../../Common/reactSelectTheme';
import { useListControls, useViewMode } from '../../../../hooks/useListControls';
import { useUrlState } from '../../../../hooks/useUrlState';
import EmailModal from './EmailModal';
import RegisterPaymentModal from './RegisterPaymentModal';

// Lucide React Icons
import {
  Plus,
  RefreshCw,
  Loader2,
  FileText,
  CreditCard,
  Link,
  Search,
  User,
  Clock,
  AlertTriangle,
  GripVertical,
  Flag,
  Undo2,
  Receipt,
  Mail,
  Download
} from 'lucide-react';

// Lane configuration
// coreLane: true = always visible (the happy path), false = only shown when populated
const LANE_CONFIG = [
  { key: 'Draft', label: 'Utkast', color: '#6b7280', bg: '#f3f4f6', showTotal: false, coreLane: false },
  { key: 'Sent', label: 'Publicerade', color: 'var(--primary)', bg: 'var(--rgba-primary-1)', showTotal: true, coreLane: true },
  { key: 'Partially_Paid', label: 'Delvis betalda', color: 'var(--bs-warning)', bg: 'rgba(255, 170, 43, 0.06)', showTotal: true, coreLane: false },
  { key: 'Paid', label: 'Betalda', color: 'var(--bs-success)', bg: 'rgba(104, 227, 101, 0.06)', showTotal: true, coreLane: true },
  { key: 'Credited', label: 'Krediterade', color: '#7c3aed', bg: 'rgba(124, 58, 237, 0.06)', showTotal: true, coreLane: false },
  { key: 'Cancelled', label: 'Makulerade', color: '#dc2626', bg: 'rgba(220, 38, 38, 0.06)', showTotal: false, coreLane: false },
];

const DATE_RANGE_OPTIONS = [
  { value: 'all', label: 'Alla datum' },
  { value: 'm3', label: 'Senaste 3 mån' },
  { value: 'year', label: 'I år' },
  { value: 'custom', label: 'Anpassat…' },
];

// Lane label lookup
const LANE_LABELS = {};
LANE_CONFIG.forEach(l => { LANE_LABELS[l.key] = l.label; });

// Drag transitions for customer invoices — adjacent steps only
//
// 6-state model: Draft → Sent → Partially_Paid → Paid (or Credited/Cancelled)
// - Draft → Sent requires publishing (PDF, invoice number) → redirect to detail
// - Payment transitions need amount + date + account → redirect to detail
// - Paid, Cancelled, Credited are terminal (no dragging from)
// Overdue invoices stay in their workflow lane (Sent/Partially_Paid)
// and are visually flagged via is_overdue — not a separate lane.
const DRAG_TRANSITIONS = {
  'Draft': ['Sent'],                     // Redirect: requires publish flow
  'Sent': ['Paid'],                      // Redirect: requires payment registration
  'Partially_Paid': ['Paid'],            // Redirect: requires payment registration
  'Paid': [],                            // Terminal
  'Cancelled': [],                       // Terminal
  'Credited': [],                        // Terminal
};

// Format currency helper
const formatCurrency = (amount) =>
  new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK', maximumFractionDigits: 0 }).format(amount);

// Invoice Card Component
const InvoiceCard = ({ invoice, onClick, getAmount, isOverdue, relationships, isDragging, onUndo, showUndo, onSendEmail, onDownload }) => {
  const isCredit = invoice.invoice_type === 'credit_note';
  const amount = getAmount(invoice);
  const isDraft = !invoice.is_published;
  const isTerminal = ['Paid', 'Cancelled', 'Credited'].includes(invoice.status);

  return (
    <div
      className={`invoice-card${isDragging ? ' is-dragging' : ''}${isTerminal ? ' drag-disabled' : ''}${isOverdue ? ' invoice-card-overdue' : ''}`}
      style={undefined}
      onClick={onClick}
    >
      {/* Top row: number + amount */}
      <div className="d-flex align-items-start gap-2 mb-2">
        <div className="card-icon" style={{
          color: isCredit ? 'var(--bs-warning)' : 'var(--primary)',
          background: isCredit ? 'rgba(255, 170, 43, 0.1)' : 'var(--rgba-primary-1)',
        }}>
          {isCredit ? <CreditCard size={12} /> : <FileText size={12} />}
        </div>
        <span
          className="card-invoice-number"
          title={isDraft ? '' : (invoice.invoicenumber || '')}
          style={{ fontWeight: '600', fontSize: '0.8125rem', color: 'var(--title)' }}
        >
          {isDraft ? 'Utkast' : (invoice.invoicenumber || '-')}
        </span>
        {!isDraft && (
          <span style={{
            fontWeight: '700',
            fontSize: '0.8125rem',
            color: isCredit ? 'var(--bs-warning)' : 'var(--title)',
            whiteSpace: 'nowrap',
            marginLeft: 'auto'
          }}>
            {isCredit ? '-' : ''}{formatCurrency(Math.abs(amount))}
          </span>
        )}
        {showUndo && (
          <button className="undo-btn" onClick={(e) => { e.stopPropagation(); if (onUndo) onUndo(invoice); }} title="Ångra">
            <Undo2 size={12} />
          </button>
        )}
        {!isTerminal && <GripVertical size={14} className="drag-handle" />}
      </div>

      {/* Customer */}
      <div className="d-flex align-items-center gap-1 mb-2" style={{ fontSize: '0.75rem', color: 'var(--secondary)' }}>
        <User size={11} style={{ opacity: 0.5 }} />
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {invoice.name || 'Ingen kund'}
        </span>
      </div>

      {/* Due date + overdue flag */}
      {invoice.deadline && !isDraft && (
        <div className="d-flex align-items-center gap-1" style={{ fontSize: '0.6875rem', color: 'var(--secondary)' }}>
          {isOverdue ? (
            <Flag size={10} style={{ color: 'var(--bs-danger)' }} />
          ) : (
            <Clock size={10} style={{ opacity: 0.4 }} />
          )}
          <span style={{
            color: isOverdue ? 'var(--bs-danger)' : undefined,
            fontWeight: isOverdue ? '600' : undefined
          }}>
            {isOverdue
              ? `Förfallen ${invoice.deadline}`
              : `Förfaller ${invoice.deadline}`}
          </span>
        </div>
      )}

      {/* Publication and recorded email delivery are separate. A missing
          timestamp does not rule out legacy or manual delivery. */}
      {!isDraft && !isCredit && (onSendEmail || onDownload) && (
        <div className="card-delivery" onClick={(e) => e.stopPropagation()}>
          {!invoice.sent_at && !isTerminal ? (
            <span className="card-unsent" title="Fakturan kan ha skickats manuellt eller innan e-post började registreras.">Ingen registrerad e-post</span>
          ) : invoice.sent_at ? (
            <span className="card-sent">Skickad {dayjs(invoice.sent_at).format('YYYY-MM-DD')}</span>
          ) : <span />}
          <span className="card-delivery-actions">
            {onSendEmail && (
              <button type="button" className="card-action" title="Skicka med e-post" onClick={() => onSendEmail(invoice)}>
                <Mail size={13} />
              </button>
            )}
            {onDownload && (
              <button type="button" className="card-action" title="Ladda ner PDF" onClick={() => onDownload(invoice)}>
                <Download size={13} />
              </button>
            )}
          </span>
        </div>
      )}

      {/* Relationship badges */}
      {relationships.length > 0 && (
        <div className="mt-2 pt-2" style={{ borderTop: '1px solid #f1f5f9' }}>
          {relationships.slice(0, 1).map((rel, i) => (
            <div key={i} className="d-flex align-items-center gap-1" style={{ fontSize: '0.6875rem' }}>
              <Link size={9} style={{ color: 'var(--bs-warning)' }} />
              <span style={{ color: 'var(--secondary)' }}>{rel.description}</span>
            </div>
          ))}
          {relationships.length > 1 && (
            <span style={{ fontSize: '0.625rem', color: 'var(--secondary)' }}>
              +{relationships.length - 1} mer
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export const InvoiceList = () => {
  const { companyId } = useParams();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  // Search + type + the overdue toggle all live in the URL: a colleague
  // should be able to paste "the overdue invoices" and see the same list.
  const {
    search: searchTerm,
    setSearch: setSearchTerm,
    debouncedSearch,
    filter: typeFilter,
    setFilter: setTypeFilter,
  } = useListControls({ defaultFilter: 'all', defaultSort: null, filterKey: 'type' });
  const [viewMode, changeView] = useViewMode('invoices', 'kanban', ['kanban', 'list']);
  const [listStatus, setListStatus] = useUrlState('status', 'all');
  const [dateRange, setDateRange] = useUrlState('date', 'all');
  const [customFrom, setCustomFrom] = useUrlState('from', '');
  const [customTo, setCustomTo] = useUrlState('to', '');
  const [overdueParam, setOverdueParam] = useUrlState('overdue', '');
  const showOnlyOverdue = overdueParam === '1';
  const setShowOnlyOverdue = (v) => setOverdueParam(v ? '1' : '');
  const [dragSourceStatus, setDragSourceStatus] = useState(null);
  const [paidDays, setPaidDays] = useState(30);
  // Invoice being e-mailed from the list (null = modal closed).
  const [emailInvoice, setEmailInvoice] = useState(null);
  // Invoice a payment is being registered on (null = modal closed).
  const [payInvoice, setPayInvoice] = useState(null);

  const history = useHistory();
  const location = useLocation();
  const publishedInvoice = invoices.find(invoice => invoice.id === location.state?.publishedInvoiceId);

  // Helper function to find relationships between invoices
  const getRelationshipInfo = (invoice, allInvoices) => {
    const relationships = [];

    if (invoice.invoice_type === 'credit_note' && invoice.related_invoice_id) {
      const relatedInvoice = allInvoices.find(inv => inv.id === invoice.related_invoice_id);
      if (relatedInvoice) {
        relationships.push({
          type: 'credit_relates_to_invoice',
          relatedInvoice,
          description: `Krediterar ${relatedInvoice.invoicenumber || 'faktura'}`
        });
      } else if (invoice.related_invoice_details) {
        relationships.push({
          type: 'credit_relates_to_invoice',
          relatedInvoice: invoice.related_invoice_details,
          description: `Krediterar ${invoice.related_invoice_details.invoice_number}`
        });
      }
    }

    if (invoice.invoice_type === 'invoice') {
      const relatedCreditNotes = allInvoices.filter(inv =>
        inv.invoice_type === 'credit_note' && inv.related_invoice_id === invoice.id
      );
      relatedCreditNotes.forEach(creditNote => {
        relationships.push({
          type: 'invoice_has_credit_note',
          relatedInvoice: creditNote,
          description: `Krediterad av ${creditNote.invoicenumber || 'kreditnota'}`
        });
      });
    }

    return relationships;
  };

  const fetchInvoiceData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const getInvoicesApiUrl = getApiUrl(`${companyId}/invoices`);
      const invoicesResult = await axios.get(getInvoicesApiUrl, { withCredentials: true });

      // The list carries the same totals as the detail endpoint; recipient
      // details are loaded per invoice only when the email modal opens.
      setInvoices(invoicesResult.data.data);
    } catch (err) {
      setError('Det gick inte att hämta fakturorna. Försök igen senare.');
      console.error('Error fetching invoice data:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [companyId]);

  const openEmailModal = async (invoice) => {
    try {
      const response = await axios.get(getApiUrl(`${companyId}/invoices/${invoice.id}`), { withCredentials: true });
      setEmailInvoice({ ...invoice, ...response.data.data, id: invoice.id });
    } catch (err) {
      console.error(`Failed to fetch details for invoice ${invoice.id}:`, err);
      toast.error('Fel', { description: 'Kunde inte hämta fakturans uppgifter. Försök igen senare.' });
    }
  };

  const handleSendEmail = async (emailDetails) => {
    if (!emailInvoice) return;
    const response = await axios.post(
      getApiUrl(`${companyId}/invoices/${emailInvoice.id}/send-email`),
      emailDetails,
      { withCredentials: true }
    );
    if (response.data.deliveryRecorded === false) {
      toast.warning('E-post skickad', { description: 'Fakturan skickades, men leveransstatus kunde inte sparas. Skicka inte igen.' });
    } else {
      toast.success('E-post skickad', {
        description: `Faktura ${emailInvoice.invoicenumber || ''} skickades till ${emailDetails.email}.`.replace('  ', ' '),
      });
    }
    fetchInvoiceData();
  };

  const handleDownloadPdf = async (invoice) => {
    try {
      const response = await axios.get(
        getApiUrl(`${companyId}/invoices/${invoice.id}/pdf`),
        { responseType: 'blob', withCredentials: true }
      );
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `faktura-${invoice.invoicenumber || invoice.id}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading invoice PDF:', err);
      toast.error('Fel', { description: 'Kunde inte hämta PDF:en. Försök igen senare.' });
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchInvoiceData();
  };

  useEffect(() => {
    fetchInvoiceData();
  }, [fetchInvoiceData]);

  const handleCardClick = (invoice) => {
    if (invoice.is_published) {
      history.push(`/${companyId}/view-invoice-new/${invoice.id}`);
    } else if (invoice.invoice_type === 'invoice') {
      history.push(`/${companyId}/edit-invoice/${invoice.id}`);
    } else if (invoice.invoice_type === 'credit_note') {
      history.push(`/${companyId}/edit-credit-note/${invoice.id}`);
    }
  };

  // Navigate to detail page for the invoice (used for drag redirects)
  const navigateToDetail = (invoice) => {
    if (invoice.is_published) {
      history.push(`/${companyId}/view-invoice-new/${invoice.id}`);
    } else {
      history.push(`/${companyId}/edit-invoice/${invoice.id}`);
    }
  };

  // --- Undo handler ---
  const handleUndo = useCallback(async (invoice) => {
    const status = invoice.status;

    if (status === 'Paid') {
      const confirm = await Swal.fire({
        title: 'Ångra betalning',
        html: `<p>Ångra betalning för <strong>${invoice.invoicenumber || ''}</strong>?</p>
               <p style="font-size:0.85rem; color:#6b7280;">Betalningen reverseras och verifikationen annulleras.</p>`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Ångra',
        cancelButtonText: 'Avbryt',
        confirmButtonColor: '#d97706'
      });
      if (!confirm.isConfirmed) return;
    } else if (status === 'Partially_Paid') {
      const confirm = await Swal.fire({
        title: 'Ångra senaste betalning',
        html: `<p>Ångra senaste betalningen för <strong>${invoice.invoicenumber || ''}</strong>?</p>
               <p style="font-size:0.85rem; color:#6b7280;">Senaste betalningen reverseras.</p>`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Ångra',
        cancelButtonText: 'Avbryt',
        confirmButtonColor: '#d97706'
      });
      if (!confirm.isConfirmed) return;
    } else {
      return;
    }

    try {
      await axios.post(
        getApiUrl(`${companyId}/invoices/${invoice.id}/undo`),
        {},
        { withCredentials: true }
      );
      toast.success(`Ångrade ${status === 'Paid' ? 'betalning' : 'senaste betalningen'} för ${invoice.invoicenumber || ''}`);
      fetchInvoiceData();
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || 'Kunde inte ångra åtgärden';
      toast.error(msg);
    }
  }, [companyId, fetchInvoiceData]);

  // --- Drag and Drop ---
  const handleDragStart = (start) => {
    setDragSourceStatus(start.source.droppableId);
  };

  const handleDragEnd = async (result) => {
    setDragSourceStatus(null);
    const { source, destination, draggableId } = result;

    if (!destination || source.droppableId === destination.droppableId) return;

    const fromStatus = source.droppableId;
    const toStatus = destination.droppableId;
    const invoice = invoices.find(inv => inv.id === draggableId);

    if (!invoice) return;

    // Validate transition
    const allowed = DRAG_TRANSITIONS[fromStatus] || [];
    if (!allowed.includes(toStatus)) {
      toast.error(`Kan inte flytta från "${LANE_LABELS[fromStatus]}" till "${LANE_LABELS[toStatus]}"`);
      return;
    }

    // Draft → Sent: publish the invoice
    if (fromStatus === 'Draft' && toStatus === 'Sent') {
      const confirm = await Swal.fire({
        title: 'Publicera faktura',
        html: `<p>Publicera <strong>${invoice.invoicenumber || 'utkast'}</strong>?</p>
               <p style="font-size:0.85rem; color:#6b7280;">Fakturanummer tilldelas, PDF genereras och verifikation skapas (fakturametoden).</p>`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Publicera',
        cancelButtonText: 'Avbryt',
        confirmButtonColor: 'var(--primary)'
      });
      if (!confirm.isConfirmed) return;

      try {
        await axios.post(
          getApiUrl(`${companyId}/invoices/${invoice.id}/publish`),
          { action: 'storeOnly' },
          { withCredentials: true }
        );
        toast.success(`Faktura ${invoice.invoicenumber || ''} publicerad`);
        fetchInvoiceData();
      } catch (err) {
        const msg = err.response?.data?.error || err.response?.data?.message || 'Kunde inte publicera fakturan';
        toast.error(msg);
      }
      return;
    }

    // Sent/Partially_Paid → Paid: register payment
    if ((fromStatus === 'Sent' || fromStatus === 'Partially_Paid') && toStatus === 'Paid') {
      // The dialog posts the payment itself; the card moves when the list
      // reloads in onRegistered.
      setPayInvoice(invoice);
      return;
    }

    // Fallback: redirect to detail
    navigateToDetail(invoice);
  };

  // Check if a lane is a valid drop target
  const isValidDropTarget = useCallback((laneKey) => {
    if (!dragSourceStatus) return true;
    const allowed = DRAG_TRANSITIONS[dragSourceStatus] || [];
    return allowed.includes(laneKey);
  }, [dragSourceStatus]);

  // Helper: check if an invoice is overdue (prefer computed is_overdue from backend)
  const isInvoiceOverdue = useCallback((inv) => {
    if (inv.is_overdue !== undefined) return inv.is_overdue;
    // Fallback: compute locally
    if (!inv.is_published || !inv.deadline) return false;
    if (inv.status === 'Paid' || inv.status === 'Cancelled' || inv.status === 'Credited') return false;
    return new Date(inv.deadline) < new Date();
  }, []);

  // Filter invoices based on type, search, and overdue toggle
  const dateBounds = useMemo(() => {
    const iso = (d) => d.toISOString().slice(0, 10);
    const today = new Date();
    switch (dateRange) {
      case 'm3': { const d = new Date(today); d.setMonth(d.getMonth() - 3); return { from: iso(d), to: null }; }
      case 'year': return { from: `${today.getFullYear()}-01-01`, to: null };
      case 'custom': return { from: customFrom || null, to: customTo || null };
      default: return { from: null, to: null };
    }
  }, [dateRange, customFrom, customTo]);

  const filteredInvoices = useMemo(() => {
    let filtered = typeFilter === 'all' ? invoices
      : invoices.filter(inv => inv.invoice_type === typeFilter);

    if (debouncedSearch) {
      const term = debouncedSearch.toLowerCase();
      filtered = filtered.filter(inv =>
        (inv.invoicenumber || '').toLowerCase().includes(term) ||
        (inv.name || '').toLowerCase().includes(term)
      );
    }

    if (dateBounds.from || dateBounds.to) {
      filtered = filtered.filter((inv) => {
        const d = (inv.deadline || '').slice(0, 10);
        if (!d) return false;
        if (dateBounds.from && d < dateBounds.from) return false;
        if (dateBounds.to && d > dateBounds.to) return false;
        return true;
      });
    }

    if (showOnlyOverdue) {
      filtered = filtered.filter(isInvoiceOverdue);
    }

    return filtered;
  }, [invoices, typeFilter, debouncedSearch, showOnlyOverdue, isInvoiceOverdue, dateBounds]);


  // Count overdue invoices (for the Förfallna chip)
  const overdueCount = useMemo(() => {
    return invoices.filter(isInvoiceOverdue).length;
  }, [invoices, isInvoiceOverdue]);

  // Status counts (for Paid lane filter display)
  const statusCounts = useMemo(() => {
    return invoices.reduce((acc, inv) => {
      acc[inv.status] = (acc[inv.status] || 0) + 1;
      return acc;
    }, {});
  }, [invoices]);

  // Group invoices by status for lanes
  // Overdue invoices go into their workflow lane (Sent or Partially_Paid)
  const groupedInvoices = useMemo(() => {
    const groups = {};
    LANE_CONFIG.forEach(lane => { groups[lane.key] = []; });
    filteredInvoices.forEach(inv => {
      const laneKey = inv.status;
      if (groups[laneKey]) {
        groups[laneKey].push(inv);
      }
    });
    Object.keys(groups).forEach(key => {
      groups[key].sort((a, b) => new Date(b.invoicedate || b.created_at) - new Date(a.invoicedate || a.created_at));
    });
    // Filter Betalda lane by recency
    if (groups['Paid'] && paidDays > 0) {
      const cutoff = new Date();
      cutoff.setDate(cutoff.getDate() - paidDays);
      groups['Paid'] = groups['Paid'].filter(inv => {
        const date = new Date(inv.invoicedate || inv.created_at);
        return date >= cutoff;
      });
    }
    return groups;
  }, [filteredInvoices, paidDays]);

  // Calculate comprehensive financial metrics
  const financialMetrics = useMemo(() => {
    const publishedInvoices = invoices.filter(inv => inv.is_published);
    const publishedRegularInvoices = publishedInvoices.filter(inv => inv.invoice_type === 'invoice');
    const publishedCreditNotes = publishedInvoices.filter(inv => inv.invoice_type === 'credit_note');

    const getAmount = (inv) => {
      let amount = 0;
      if (inv.totals && inv.totals.grand_total !== undefined) {
        amount = inv.totals.grand_total;
      } else if (inv.total_with_tax !== undefined) {
        amount = inv.total_with_tax;
      } else if (inv.grand_total !== undefined) {
        amount = inv.grand_total;
      } else if (inv.total_amount !== undefined) {
        amount = inv.total_amount;
      }
      return parseFloat(amount) || 0;
    };

    const totalInvoiceAmount = publishedRegularInvoices.reduce((sum, inv) => sum + getAmount(inv), 0);
    const totalCreditAmount = publishedCreditNotes.reduce((sum, inv) => sum + Math.abs(getAmount(inv)), 0);
    const netRevenue = totalInvoiceAmount - totalCreditAmount;

    const paidInvoices = publishedRegularInvoices.filter(inv => inv.status === 'Paid');
    const overdueInvoices = publishedRegularInvoices.filter(inv => inv.is_overdue);
    const pendingInvoices = publishedRegularInvoices.filter(inv => inv.status === 'Sent');

    const paidAmount = paidInvoices.reduce((sum, inv) => sum + getAmount(inv), 0);
    const overdueAmount = overdueInvoices.reduce((sum, inv) => sum + getAmount(inv), 0);
    const pendingAmount = pendingInvoices.reduce((sum, inv) => sum + getAmount(inv), 0);

    return {
      netRevenue, paidAmount, overdueAmount, pendingAmount,
      publishedCount: publishedInvoices.length,
      paidCount: paidInvoices.length,
      overdueCount: overdueInvoices.length,
      pendingCount: pendingInvoices.length,
      collectionRate: totalInvoiceAmount > 0 ? (paidAmount / totalInvoiceAmount) * 100 : 0,
      getAmount
    };
  }, [invoices]);

  // Count totals for filters
  const filteredTotals = useMemo(() => {
    return {
      totalCount: filteredInvoices.length,
      invoiceCount: invoices.filter(inv => inv.invoice_type === 'invoice').length,
      creditNoteCount: invoices.filter(inv => inv.invoice_type === 'credit_note').length,
    };
  }, [filteredInvoices, invoices]);

  // Visible lanes
  const visibleLanes = useMemo(() => {
    return LANE_CONFIG.filter(lane => lane.coreLane || (groupedInvoices[lane.key] || []).length > 0);
  }, [groupedInvoices]);

  if (loading && !refreshing) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Laddar...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <>
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .spin { animation: spin 1s linear infinite; }

        /* Stats Grid */
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1.25rem;
        }
        .stat-card {
          background: white;
          border: none;
          border-radius: 8px;
          padding: 1.5rem;
          text-align: center;
          transition: all 0.3s ease;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          position: relative;
          overflow: hidden;
        }
        .stat-card:hover {
          box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
        }
        .stat-icon {
          width: 48px; height: 48px;
          border-radius: 12px;
          display: flex; align-items: center; justify-content: center;
          margin: 0 auto 1rem;
          position: relative;
        }
        .stat-value { font-size: 1.5rem; font-weight: 700; margin-bottom: 0.375rem; }
        .stat-label { color: var(--secondary); font-size: 0.75rem; font-weight: 500; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 0.25rem; }
        .stat-subtitle { color: var(--secondary); font-size: 0.6875rem; }

        /* Kanban Board */
        .invoice-list-table {
          background: #fff;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          overflow: hidden;
        }
        .invoice-list-table tbody tr:hover { background: #f9fafb; }
        .invoice-board {
          display: flex;
          gap: 0.75rem;
          align-items: flex-start;
          overflow-x: auto;
          padding-bottom: 1rem;
        }
        .invoice-lane {
          flex: 1;
          min-width: 0;
          background: #f8f9fb;
          border-radius: 12px;
          display: flex;
          flex-direction: column;
          max-height: calc(100vh - 340px);
          min-height: 400px;
          transition: all 0.2s ease;
        }
        .invoice-lane.lane-empty {
          background: transparent;
          border: 2px dashed #b0b8c4;
          min-height: 200px;
          opacity: 0.6;
        }
        .invoice-lane.lane-empty .lane-header {
          background: transparent;
        }
        .lane-empty-placeholder {
          text-align: center;
          padding: 1.5rem 0.75rem;
          color: var(--secondary);
          font-size: 0.75rem;
          opacity: 0.5;
        }

        /* Drop target feedback */
        .invoice-lane.lane-drag-over {
          background: rgba(59, 130, 246, 0.06);
          box-shadow: inset 0 0 0 2px var(--primary);
          border-radius: 12px;
        }
        .invoice-lane.lane-drop-invalid {
          opacity: 0.3;
          filter: grayscale(0.5);
        }

        .lane-header {
          padding: 0.875rem 1rem;
          background: white;
          border-radius: 12px 12px 0 0;
          border-bottom: 1px solid #f1f5f9;
        }
        .lane-date-filter {
          display: block;
          width: 100%;
          margin-top: 0.5rem;
          padding: 0.25rem 0.375rem;
          font-size: 0.6875rem;
          color: var(--secondary);
          background: #f8f9fb;
          border: 1px solid #e2e8f0;
          border-radius: 4px;
          cursor: pointer;
          outline: none;
        }
        .lane-date-filter:focus {
          border-color: var(--primary);
        }
        .lane-count {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 24px;
          height: 24px;
          border-radius: 12px;
          font-size: 0.75rem;
          font-weight: 600;
          padding: 0 0.5rem;
        }
        .lane-cards {
          padding: 0.5rem;
          overflow-y: auto;
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }
        .lane-cards::-webkit-scrollbar { width: 4px; }
        .lane-cards::-webkit-scrollbar-track { background: transparent; }
        .lane-cards::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 4px; }

        /* Invoice Card */
        .invoice-card {
          background: white;
          border: 1px solid #e8ecf1;
          border-radius: 8px;
          padding: 0.75rem 0.875rem;
          cursor: grab;
          transition: all 0.15s ease;
          user-select: none;
        }
        .invoice-card:hover {
          border-color: var(--primary);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
        }
        .invoice-card.is-dragging {
          box-shadow: 0 12px 28px rgba(0, 0, 0, 0.15);
          border-color: var(--primary);
          opacity: 0.95;
        }
        .invoice-card.drag-disabled {
          cursor: default;
        }
        .invoice-card-overdue {
          background: #fff5f5;
        }
        .card-delivery {
          display: flex;
          align-items: center;
          gap: 0.375rem;
          margin-top: 0.5rem;
          padding-top: 0.5rem;
          border-top: 1px solid #f1f5f9;
          font-size: 0.6875rem;
        }
        .card-unsent {
          display: inline-block;
          padding: 1px 7px;
          border-radius: 999px;
          background: #fef3c7;
          color: #92400e;
          font-size: 0.6875rem;
          font-weight: 600;
        }
        .card-sent { color: var(--secondary); }
        .card-delivery-actions {
          display: inline-flex;
          gap: 0.125rem;
          margin-left: auto;
        }
        .card-action {
          width: 24px;
          height: 24px;
          border: 0;
          border-radius: 6px;
          background: transparent;
          color: #6b7280;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
        }
        .card-action:hover { background: var(--rgba-primary-1); color: var(--primary); }
        .undo-btn {
          opacity: 0;
          border: none;
          background: rgba(245, 158, 11, 0.1);
          color: #d97706;
          border-radius: 4px;
          padding: 2px 4px;
          cursor: pointer;
          transition: opacity 0.15s;
          flex-shrink: 0;
        }
        .invoice-card:hover .undo-btn {
          opacity: 1;
        }
        .undo-btn:hover {
          background: rgba(245, 158, 11, 0.2);
        }
        .drag-handle {
          color: #d1d5db;
          opacity: 0;
          transition: opacity 0.15s ease;
          flex-shrink: 0;
        }
        .invoice-card:hover .drag-handle {
          opacity: 1;
        }
        .card-invoice-number {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          min-width: 0;
          flex: 1;
        }
        .card-icon {
          width: 22px; height: 22px;
          border-radius: 5px;
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0;
        }

        /* Search input */
        .board-search {
          position: relative;
        }
        .board-search input {
          padding-left: 2.25rem;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          font-size: 0.875rem;
          height: 38px;
          width: 280px;
          transition: all 0.2s ease;
          background: white;
        }
        .board-search input:focus {
          border-color: var(--primary);
          box-shadow: 0 0 0 3px var(--rgba-primary-1);
          outline: none;
        }
        .board-search .search-icon {
          position: absolute;
          left: 0.75rem;
          top: 50%;
          transform: translateY(-50%);
          color: var(--secondary);
          opacity: 0.5;
        }

        @media (max-width: 1024px) {
          .stats-grid { grid-template-columns: repeat(2, 1fr); }
        }
        @media (max-width: 768px) {
          .stats-grid { grid-template-columns: 1fr 1fr; }
        }
      `}</style>

      <PageHeader
        icon={Receipt}
        title="Kundfakturor"
        subtitle="Skapa, skicka och följ upp dina kundfakturor."
      />
      <div className="col-12">
        {publishedInvoice && (
          <Alert variant="success" dismissible onClose={() => {
            const { publishedInvoiceId, ...rest } = location.state || {};
            history.replace({ ...location, state: rest });
          }}>
            <Alert.Heading>Faktura {publishedInvoice.invoicenumber} är publicerad</Alert.Heading>
            <p>{publishedInvoice.customername || publishedInvoice.name}. Välj hur du vill leverera fakturan.</p>
            <div className="d-flex gap-2 flex-wrap">
              <Button onClick={() => openEmailModal(publishedInvoice)}>
                <Mail size={16} className="me-2" />{publishedInvoice.sent_at ? 'Skicka igen med e-post' : 'Skicka med e-post'}
              </Button>
              <Button variant="outline-primary" onClick={() => handleDownloadPdf(publishedInvoice)}>
                <Download size={16} className="me-2" />Ladda ner PDF
              </Button>
            </div>
          </Alert>
        )}
        {error && (
          <Alert variant="danger" onClose={() => setError(null)} dismissible>
            {error}
          </Alert>
        )}

            {/* Financial Dashboard */}
            <div className="stats-grid mb-4">
              <div className="stat-card">
                <div className="stat-icon" style={{ background: 'var(--rgba-primary-1)', color: 'var(--primary)' }}>
                  <FileText size={24} />
                </div>
                <div className="stat-value" style={{ color: 'var(--primary)' }}>
                  {formatCurrency(financialMetrics.netRevenue)}
                </div>
                <div className="stat-label">Nettointäkter</div>
                <div className="stat-subtitle">Fakturor - Kreditnotor</div>
              </div>

              <div className="stat-card">
                <div className="stat-icon" style={{ background: 'var(--rgba-primary-1)', color: 'var(--primary)' }}>
                  <CreditCard size={24} />
                </div>
                <div className="stat-value" style={{ color: 'var(--primary)' }}>
                  {financialMetrics.collectionRate.toFixed(1)}%
                </div>
                <div className="stat-label">Indrivningsgrad</div>
                <div className="stat-subtitle">{financialMetrics.paidCount} av {financialMetrics.publishedCount} betalda</div>
              </div>

              <div className="stat-card">
                <div className="stat-icon" style={{ background: 'var(--rgba-primary-1)', color: 'var(--primary)' }}>
                  <Loader2 size={24} />
                </div>
                <div className="stat-value" style={{ color: 'var(--primary)' }}>
                  {formatCurrency(financialMetrics.pendingAmount)}
                </div>
                <div className="stat-label">Utestående</div>
                <div className="stat-subtitle">{financialMetrics.pendingCount} publicerade fakturor</div>
              </div>

              <div className="stat-card">
                <div className="stat-icon" style={{ background: 'var(--rgba-primary-1)', color: 'var(--primary)' }}>
                  <AlertTriangle size={24} />
                </div>
                <div className="stat-value" style={{ color: 'var(--primary)' }}>
                  {formatCurrency(financialMetrics.overdueAmount)}
                </div>
                <div className="stat-label">Förfallna</div>
                <div className="stat-subtitle">{financialMetrics.overdueCount} förfallna fakturor</div>
              </div>
            </div>

            <ListToolbar
              className="mb-3"
              search={{
                value: searchTerm,
                onChange: setSearchTerm,
                placeholder: 'Sök fakturanummer eller kund…',
                ariaLabel: 'Sök bland kundfakturor',
              }}
              view={{ value: viewMode, onChange: changeView, modes: ['kanban', 'list'] }}
            >
              <select
                className="form-select form-select-sm"
                style={{ width: 'auto', fontSize: '0.875rem', border: '1px solid #e2e8f0', borderRadius: '8px', height: '38px' }}
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
              >
                <option value="all">Alla ({filteredTotals.totalCount})</option>
                <option value="invoice">Fakturor ({filteredTotals.invoiceCount})</option>
                <option value="credit_note">Kreditnotor ({filteredTotals.creditNoteCount})</option>
              </select>
              <div style={{ minWidth: 160 }}>
                <Select
                  options={DATE_RANGE_OPTIONS}
                  value={DATE_RANGE_OPTIONS.find((o) => o.value === dateRange) || DATE_RANGE_OPTIONS[0]}
                  onChange={(opt) => setDateRange(opt?.value || 'all')}
                  isSearchable={false}
                  theme={brandSelectTheme}
                  menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                  styles={{
                    control: (b) => ({ ...b, minHeight: 38, fontSize: 13 }),
                    option: (b) => ({ ...b, fontSize: 13 }),
                    menuPortal: (b) => ({ ...b, zIndex: 9999 }),
                  }}
                  aria-label="Filtrera på förfallodatum"
                />
              </div>
              {dateRange === 'custom' && (
                <>
                  <input type="date" className="form-control" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} aria-label="Från datum" style={{ width: 150, height: 38 }} />
                  <span style={{ color: '#9ca3af', fontSize: 13 }}>–</span>
                  <input type="date" className="form-control" value={customTo} onChange={(e) => setCustomTo(e.target.value)} aria-label="Till datum" style={{ width: 150, height: 38 }} />
                </>
              )}
              <Button
                variant="light"
                size="sm"
                onClick={handleRefresh}
                disabled={refreshing}
                style={{ height: '38px', width: '38px', padding: 0, borderRadius: '8px', border: '1px solid #e2e8f0' }}
              >
                <RefreshCw size={16} className={refreshing ? 'spin' : ''} style={{ color: 'var(--secondary)' }} />
              </Button>
              <Button variant="primary" onClick={() => history.push(`/${companyId}/create-invoices`)}>
  <Plus size={16} className="me-2" />
  Ny faktura
  </Button>
          </ListToolbar>

            {/* Status is an exclusive set and only narrows the list view (the
                board already splits by status). Overdue is not a status — an
                overdue invoice is also Skickad or Delbetald — so it is a toggle
                after the divider, and it applies to both views. */}
            <div className="ll-chips mb-3" role="group" aria-label="Filtrera kundfakturor">
              {viewMode === 'list' && (
                <>
                  {[
                    { key: 'all', label: 'Alla', count: filteredInvoices.length },
                    ...LANE_CONFIG.map((lane) => ({
                      key: lane.key,
                      label: lane.label,
                      count: filteredInvoices.filter((i) => i.status === lane.key).length,
                    })),
                  ].map((chip) => (
                    <button
                      key={chip.key}
                      type="button"
                      className={`ll-chip ${listStatus === chip.key ? 'active' : ''}`}
                      onClick={() => setListStatus(chip.key)}
                      aria-pressed={listStatus === chip.key}
                    >
                      {chip.label}
                      <span className="ll-chip__count">{chip.count}</span>
                    </button>
                  ))}
                  <span className="ll-chips__divider" aria-hidden="true" />
                </>
              )}
              <button
                type="button"
                className={`ll-chip ll-chip--toggle ${showOnlyOverdue ? 'active' : ''}`}
                onClick={() => setShowOnlyOverdue(!showOnlyOverdue)}
                aria-pressed={showOnlyOverdue}
                title="Visa bara fakturor som passerat förfallodatum"
              >
                <Flag size={14} strokeWidth={1.75} className="ll-chip__icon" />
                Förfallna
                <span className="ll-chip__count">{overdueCount}</span>
              </button>
            </div>

            {refreshing && (
              <div className="text-center mb-3" style={{ fontSize: '0.8125rem', color: 'var(--secondary)' }}>
                <Loader2 size={14} className="me-1 spin" />
                Uppdaterar...
              </div>
            )}

            {invoices.length === 0 && !loading ? (
              <EmptyState
                icon={Receipt}
                title="Inga fakturor ännu"
                description="Skapa din första kundfaktura — den hamnar sedan här och följer sin egen väg från utkast till betald."
                actions={
                  <Button variant="primary" onClick={() => history.push(`/${companyId}/create-invoices`)}>
                    <Plus size={16} className="me-2" />
                    Skapa ny faktura
                  </Button>
                }
              />
            ) : viewMode === 'list' ? (
              <div className="invoice-list-table">
                <table className="table modern-table w-100 mb-0">
                  <thead>
                    <tr>
                      <th>Faktura</th>
                      <th>Kund</th>
                      <th>Förfaller</th>
                      <th className="text-end">Belopp</th>
                      <th>Status</th>
                      <th className="text-end"><span className="visually-hidden">Åtgärder</span></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredInvoices
                      .filter((inv) => listStatus === 'all' || inv.status === listStatus)
                      .map((inv) => {
                      const overdue = isInvoiceOverdue(inv);
                      const lane = LANE_CONFIG.find((l) => l.key === inv.status);
                      return (
                        <tr
                          key={inv.id}
                          onClick={() => handleCardClick(inv)}
                          style={{ cursor: 'pointer' }}
                        >
                          <td style={{ fontWeight: 600 }}>{inv.is_published ? (inv.invoicenumber || '-') : 'Utkast'}</td>
                          <td>{inv.name || 'Ingen kund'}</td>
                          <td style={{ color: overdue ? 'var(--bs-danger)' : undefined, fontWeight: overdue ? 600 : undefined }}>
                            {inv.deadline || '—'}
                          </td>
                          <td className="text-end" style={{ fontVariantNumeric: 'tabular-nums' }}>
                            {formatCurrency(financialMetrics.getAmount(inv))}
                          </td>
                          <td>
                            <span
                              style={{
                                background: lane?.bg || '#f3f4f6',
                                color: lane?.color || '#6b7280',
                                borderRadius: 999,
                                padding: '2px 10px',
                                fontSize: 12,
                                fontWeight: 500,
                              }}
                            >
                              {LANE_LABELS[inv.status] || inv.status}
                            </span>
                            {Boolean(inv.is_published) && !inv.sent_at && inv.invoice_type !== 'credit_note'
                              && ['Sent', 'Partially_Paid'].includes(inv.status) && (
                              <span className="card-unsent ms-2" title="Fakturan kan ha skickats manuellt eller innan e-post började registreras.">Ingen registrerad e-post</span>
                            )}
                          </td>
                          <td className="text-end" style={{ width: '1%', whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
                            {Boolean(inv.is_published) && inv.invoice_type !== 'credit_note' && (
                              <div className="ll-row-actions">
                                <button type="button" title="Skicka med e-post" aria-label="Skicka med e-post" onClick={() => openEmailModal(inv)}>
                                  <Mail size={14} />
                                </button>
                                <button type="button" title="Ladda ner PDF" aria-label="Ladda ner PDF" onClick={() => handleDownloadPdf(inv)}>
                                  <Download size={14} />
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              /* Kanban board with drag & drop */
              <DragDropContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
              <div className="invoice-board">
                {visibleLanes.map(lane => {
                  const laneInvoices = groupedInvoices[lane.key] || [];
                  const isEmpty = laneInvoices.length === 0;
                  const isTerminalLane = lane.key === 'Paid';
                  const isValidDrop = isValidDropTarget(lane.key);
                  const total = laneInvoices.reduce((sum, inv) => sum + Math.abs(financialMetrics.getAmount(inv)), 0);
                  const laneOverdueCount = laneInvoices.filter(isInvoiceOverdue).length;
                  const now = new Date();

                  return (
                    <Droppable
                      droppableId={lane.key}
                      key={lane.key}
                      isDropDisabled={!isValidDrop}
                    >
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          className={[
                            'invoice-lane',
                            isEmpty && !snapshot.isDraggingOver ? 'lane-empty' : '',
                            snapshot.isDraggingOver ? 'lane-drag-over' : '',
                            dragSourceStatus && !isValidDrop ? 'lane-drop-invalid' : ''
                          ].filter(Boolean).join(' ')}
                        >
                          {/* Lane header */}
                          <div className="lane-header">
                            <div className="d-flex align-items-center justify-content-between mb-1">
                              <span style={{ fontWeight: '600', fontSize: '0.8125rem', color: isEmpty && !snapshot.isDraggingOver ? 'var(--secondary)' : 'var(--title)' }}>
                                {lane.label}
                              </span>
                              {(!isEmpty || snapshot.isDraggingOver) && (
                                <span className="lane-count" style={{ background: lane.bg, color: lane.color }}>
                                  {isTerminalLane && paidDays > 0 && statusCounts['Paid']
                                    ? `${laneInvoices.length} av ${statusCounts['Paid']}`
                                    : laneInvoices.length}
                                </span>
                              )}
                            </div>
                            {lane.showTotal && !isEmpty && (
                              <div style={{ fontSize: '0.75rem', fontWeight: '600', color: lane.color }}>
                                {formatCurrency(total)}
                              </div>
                            )}
                            {laneOverdueCount > 0 && (
                              <div className="d-flex align-items-center gap-1 mt-1" style={{ fontSize: '0.6875rem', color: 'var(--bs-danger)', fontWeight: '500' }}>
                                <Flag size={10} />
                                {laneOverdueCount} förfallna
                              </div>
                            )}
                            {isTerminalLane && (
                              <select
                                className="lane-date-filter"
                                value={paidDays}
                                onChange={(e) => setPaidDays(Number(e.target.value))}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <option value={7}>Senaste 7 dagar</option>
                                <option value={30}>Senaste 30 dagar</option>
                                <option value={90}>Senaste 90 dagar</option>
                                <option value={0}>Visa alla</option>
                              </select>
                            )}
                          </div>

                          {/* Lane cards */}
                          <div className="lane-cards">
                            {isEmpty && !snapshot.isDraggingOver ? (
                              <div className="lane-empty-placeholder">
                                {dragSourceStatus && isValidDrop
                                  ? 'Släpp här'
                                  : 'Inga fakturor'}
                              </div>
                            ) : (
                              laneInvoices.map((invoice, index) => {
                                const isDragDisabled = ['Paid', 'Cancelled', 'Credited'].includes(invoice.status);
                                const isOverdue = invoice.is_overdue !== undefined
                                  ? invoice.is_overdue
                                  : (!invoice.is_published ? false
                                    : invoice.deadline && new Date(invoice.deadline) < now && invoice.status !== 'Paid');
                                const relationships = getRelationshipInfo(invoice, invoices);

                                return (
                                  <Draggable
                                    key={invoice.id}
                                    draggableId={invoice.id}
                                    index={index}
                                    isDragDisabled={isDragDisabled}
                                  >
                                    {(provided, snapshot) => {
                                      const child = (
                                        <div
                                          ref={provided.innerRef}
                                          {...provided.draggableProps}
                                          {...provided.dragHandleProps}
                                        >
                                          <InvoiceCard
                                            invoice={invoice}
                                            onClick={() => !snapshot.isDragging && handleCardClick(invoice)}
                                            getAmount={financialMetrics.getAmount}
                                            isOverdue={isOverdue}
                                            onSendEmail={openEmailModal}
                                            onDownload={handleDownloadPdf}
                                            relationships={relationships}
                                            isDragging={snapshot.isDragging}
                                            showUndo={lane.key === 'Partially_Paid' || lane.key === 'Paid'}
                                            onUndo={handleUndo}
                                          />
                                        </div>
                                      );
                                      if (snapshot.isDragging) {
                                        return ReactDOM.createPortal(child, document.body);
                                      }
                                      return child;
                                    }}
                                  </Draggable>
                                );
                              })
                            )}
                            {provided.placeholder}
                          </div>
                        </div>
                      )}
                    </Droppable>
                  );
                })}
              </div>
            </DragDropContext>
            )}
      </div>

      <EmailModal
        show={Boolean(emailInvoice)}
        handleClose={() => setEmailInvoice(null)}
        invoiceData={emailInvoice}
        handleSendEmail={handleSendEmail}
      />

      <RegisterPaymentModal
        show={Boolean(payInvoice)}
        onClose={() => setPayInvoice(null)}
        companyId={companyId}
        invoice={payInvoice && {
          id: payInvoice.id,
          number: payInvoice.invoicenumber,
          customerName: payInvoice.name,
          invoiceDate: payInvoice.invoicedate,
        }}
        remaining={parseFloat(payInvoice?.amount_remaining || payInvoice?.total_with_tax || 0)}
        onRegistered={({ amount }) => {
          toast.success(`Betalning ${formatCurrency(amount)} registrerad för ${payInvoice.invoicenumber}`);
          setPayInvoice(null);
          fetchInvoiceData();
        }}
      />
    </>
  );
};

export default InvoiceList;
